import cn from "@/utils/cn"
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { useCallback, useEffect, useRef, useState } from "react"

type CustomerSupportTableProps<TData> = {
  data: TData[]
  columns: ColumnDef<TData>[]
}

const CustomerSupportTable = <TData,>({
  data,
  columns,
}: CustomerSupportTableProps<TData>) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const checkScrollability = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 30)
    }
  }, [])

  const handleChevronClick = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: 200,
        behavior: "smooth",
      })
    }
  }, [])

  useEffect(() => {
    // Use setTimeout to ensure DOM has updated
    const timeoutId = setTimeout(() => {
      checkScrollability()
    }, 0)

    const container = scrollContainerRef.current
    if (container) {
      container.addEventListener("scroll", checkScrollability)
      window.addEventListener("resize", checkScrollability)
      return () => {
        clearTimeout(timeoutId)
        container.removeEventListener("scroll", checkScrollability)
        window.removeEventListener("resize", checkScrollability)
      }
    }
    return () => clearTimeout(timeoutId)
  }, [data, columns, checkScrollability])

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: "onChange",
    defaultColumn: {
      minSize: 100,
      size: 150,
    },
  })

  return (
    <div className="relative">
      <div ref={scrollContainerRef} className="overflow-x-auto">
        <table className="w-full">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="border-b border-gray-200">
                {headerGroup.headers.map((header) => {
                  const isActionColumn = header.column.id === "actions"

                  return (
                    <th
                      key={header.id}
                      className={cn(
                        "relative whitespace-nowrap px-3 py-3 text-left text-xs font-semibold uppercase text-primary",
                        isActionColumn && "sticky z-10 bg-white"
                      )}
                      style={{
                        right: "0px",
                        width: isActionColumn ? "0px" : undefined,
                        minWidth: isActionColumn
                          ? undefined
                          : `${header.column.columnDef.minSize || 100}px`,
                      }}
                    >
                      {isActionColumn && canScrollRight && (
                        <button
                          type="button"
                          onClick={handleChevronClick}
                          className="material-icons absolute left-0 top-1/2 -translate-y-1/2 cursor-pointer text-primary/60 transition-colors hover:text-primary"
                        >
                          chevron_right
                        </button>
                      )}
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </th>
                  )
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row, index) => (
                <tr
                  key={row.id}
                  className={cn(
                    "border-b border-gray-200",
                    index % 2 === 0 ? "bg-primary/5" : "bg-white"
                  )}
                >
                  {row.getVisibleCells().map((cell) => {
                    const isActionColumn = cell.column.id === "actions"
                    const isCsNotesColumn = cell.column.id === "csNotes"
                    return (
                      <td
                        key={cell.id}
                        className={cn(
                          "px-3 py-3 text-sm text-primary/90",
                          isActionColumn &&
                          "sticky z-10 ml-5 whitespace-nowrap bg-white",
                          isCsNotesColumn && "pr-10"
                        )}
                        style={{
                          right: "0px",
                          minWidth: isActionColumn
                            ? undefined
                            : `${cell.column.columnDef.minSize}px`,
                        }}
                      >
                        {isActionColumn && (
                          <div
                            className="pointer-events-none absolute left-[-50px] top-0 h-full w-[50px]"
                            style={{
                              background:
                                "linear-gradient(to right, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1))",
                            }}
                          />
                        )}
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    )
                  })}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-3 py-8 text-center text-sm italic text-primary/60"
                >
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default CustomerSupportTable
